import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { getSegments, createSegment, deleteSegment, getSegmentContacts, previewSegment, type WebSegment } from '@/services'
import { AppButton, EmptyState, Badge, IconButton, CardGridSkeleton, Skeleton, TableSkeleton, rowActivation } from '@/components/platform/primitives'
import { Icon } from '@/components/platform/Icon'

interface Criteria {
  min_score?: number
  max_score?: number
  is_identified?: boolean
  converted?: boolean
  utm_source?: string
  last_seen_within_days?: number
}

function describeCriteria(match: Criteria): string[] {
  const pills: string[] = []
  if (match.min_score != null) pills.push(`score ≥ ${match.min_score}`)
  if (match.max_score != null) pills.push(`score ≤ ${match.max_score}`)
  if (match.is_identified === true) pills.push('identificado')
  if (match.is_identified === false) pills.push('anónimo')
  if (match.converted === true) pills.push('convirtió')
  if (match.converted === false) pills.push('no convirtió')
  if (match.utm_source) pills.push(`origen "${match.utm_source}"`)
  if (match.last_seen_within_days != null) pills.push(`visto ≤ ${match.last_seen_within_days}d`)
  return pills.length ? pills : ['todos los contactos']
}

type View = { mode: 'list' } | { mode: 'create' } | { mode: 'detail'; segment: WebSegment }

export function SegmentsPage() {
  const qc = useQueryClient()
  const [view, setView] = useState<View>({ mode: 'list' })

  const { data: result, isLoading } = useQuery({ queryKey: ['segments'], queryFn: getSegments })
  const segments = result?.data || []

  if (view.mode === 'create') {
    return <SegmentEditor onCancel={() => setView({ mode: 'list' })} onSaved={() => { qc.invalidateQueries({ queryKey: ['segments'] }); setView({ mode: 'list' }) }} />
  }
  if (view.mode === 'detail') {
    return <SegmentDetail segment={view.segment} onBack={() => setView({ mode: 'list' })} onDeleted={() => { qc.invalidateQueries({ queryKey: ['segments'] }); setView({ mode: 'list' }) }} />
  }

  return (
    <>
      <div className="page-header">
        <div className="page-title-block">
          <div className="eyebrow">Marketing</div>
          <h1>Segmentos</h1>
          <div className="subtitle">{isLoading ? <Skeleton width={360} height={14} /> : `${segments.length} segmentos guardados · listas dinámicas que se actualizan solas según las reglas.`}</div>
        </div>
        <div className="page-actions">
          <AppButton variant="primary" icon="plus" onClick={() => setView({ mode: 'create' })}>Nuevo segmento</AppButton>
        </div>
      </div>

      {isLoading ? (
        <CardGridSkeleton columns={3} cards={6} />
      ) : segments.length === 0 ? (
        <div className="card"><div className="card-body"><EmptyState icon="list" title="Todavía no hay segmentos" desc='Creá tu primera lista dinámica — ej: "Calientes sin convertir de la última semana".' /></div></div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
          {segments.map((s) => (
            <div key={s.id} className="card" role="button" aria-label={`Ver segmento ${s.name}`} onClick={() => setView({ mode: 'detail', segment: s })} {...rowActivation(() => setView({ mode: 'detail', segment: s }))} style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column' }}>
              <div style={{ padding: 16 }}>
                <div className="row gap-2" style={{ alignItems: 'center', marginBottom: 4 }}>
                  <Icon name="list" size={15} />
                  <span style={{ fontFamily: 'Bricolage Grotesque', fontWeight: 700, fontSize: 15 }}>{s.name}</span>
                </div>
                <div className="row gap-1" style={{ flexWrap: 'wrap', margin: '10px 0' }}>
                  {describeCriteria((s.match || {}) as Criteria).map((p, i) => <Badge key={i} tone="neutral" size="sm">{p}</Badge>)}
                </div>
                <div style={{ fontFamily: 'Bricolage Grotesque', fontWeight: 800, fontSize: 28, color: 'var(--bisne-green-800)' }}>
                  {s.contact_count ?? '—'} <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-3)' }}>contactos</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  )
}

function SegmentEditor({ onCancel, onSaved }: { onCancel: () => void; onSaved: () => void }) {
  const [name, setName] = useState('')
  const [criteria, setCriteria] = useState<Criteria>({ min_score: 70 })
  const [count, setCount] = useState<number | null>(null)

  const set = (patch: Partial<Criteria>) => { setCriteria((c) => ({ ...c, ...patch })); setCount(null) }

  const previewMutation = useMutation({
    mutationFn: () => previewSegment(criteria as Record<string, unknown>),
    onSuccess: (data) => setCount(Number((data as any).count ?? (data as any).recipients ?? (Array.isArray((data as any).data) ? (data as any).data.length : 0))),
    onError: (e: any) => toast.error(e?.message || 'No se pudo previsualizar'),
  })

  const saveMutation = useMutation({
    mutationFn: () => createSegment({ name: name.trim(), match: criteria as Record<string, unknown> }),
    onSuccess: () => { toast.success('Segmento creado'); onSaved() },
    onError: (e: any) => toast.error(e?.message || 'No se pudo guardar el segmento'),
  })

  return (
    <>
      <div className="page-header">
        <div className="page-title-block">
          <div className="row gap-2" style={{ marginBottom: 6 }}>
            <button className="link-btn" onClick={onCancel} type="button" style={{ fontSize: 12.5, color: 'var(--text-3)', background: 'none', border: 'none', cursor: 'pointer' }}>← Segmentos</button>
          </div>
          <h1>Nuevo segmento</h1>
          <div className="subtitle">Definí los criterios; el contador se calcula contra los contactos reales.</div>
        </div>
        <div className="page-actions">
          <AppButton variant="ghost" onClick={onCancel}>Cancelar</AppButton>
          <AppButton variant="success" icon="check" disabled={!name.trim() || saveMutation.isPending} onClick={() => saveMutation.mutate()}>Crear segmento</AppButton>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr', gap: 16 }}>
        <div className="card">
          <div className="card-header"><h3>Criterios</h3></div>
          <div className="card-body col gap-4">
            <div className="form-row">
              <label htmlFor="sg-name">Nombre del segmento</label>
              <input id="sg-name" className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder='ej. Calientes sin convertir' />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div className="form-row">
                <label htmlFor="sg-minscore">Score mínimo</label>
                <input id="sg-minscore" className="input" type="number" value={criteria.min_score ?? ''} onChange={(e) => set({ min_score: e.target.value === '' ? undefined : Number(e.target.value) })} />
              </div>
              <div className="form-row">
                <label htmlFor="sg-maxscore">Score máximo</label>
                <input id="sg-maxscore" className="input" type="number" value={criteria.max_score ?? ''} onChange={(e) => set({ max_score: e.target.value === '' ? undefined : Number(e.target.value) })} />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div className="form-row">
                <label htmlFor="sg-identified">Identificación</label>
                <select id="sg-identified" className="select" value={criteria.is_identified === undefined ? '' : String(criteria.is_identified)} onChange={(e) => set({ is_identified: e.target.value === '' ? undefined : e.target.value === 'true' })}>
                  <option value="">Cualquiera</option>
                  <option value="true">Identificado</option>
                  <option value="false">Anónimo</option>
                </select>
              </div>
              <div className="form-row">
                <label htmlFor="sg-converted">Conversión</label>
                <select id="sg-converted" className="select" value={criteria.converted === undefined ? '' : String(criteria.converted)} onChange={(e) => set({ converted: e.target.value === '' ? undefined : e.target.value === 'true' })}>
                  <option value="">Cualquiera</option>
                  <option value="true">Convirtió</option>
                  <option value="false">No convirtió</option>
                </select>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div className="form-row">
                <label htmlFor="sg-utm">Origen (utm_source)</label>
                <input id="sg-utm" className="input" value={criteria.utm_source ?? ''} onChange={(e) => set({ utm_source: e.target.value || undefined })} placeholder="ej. google" />
              </div>
              <div className="form-row">
                <label htmlFor="sg-lastseen">Visto en últimos N días</label>
                <input id="sg-lastseen" className="input" type="number" value={criteria.last_seen_within_days ?? ''} onChange={(e) => set({ last_seen_within_days: e.target.value === '' ? undefined : Number(e.target.value) })} />
              </div>
            </div>
          </div>
        </div>

        <div className="card" style={{ alignSelf: 'flex-start' }}>
          <div className="card-header">
            <h3>Vista previa</h3>
            <AppButton variant="success" size="sm" icon="refresh" onClick={() => previewMutation.mutate()}>Calcular</AppButton>
          </div>
          <div className="card-body">
            <div className="row gap-1" style={{ flexWrap: 'wrap', marginBottom: 14 }}>
              {describeCriteria(criteria).map((p, i) => <Badge key={i} tone="neutral" size="sm">{p}</Badge>)}
            </div>
            {count === null ? (
              <div className="muted" style={{ fontSize: 13, textAlign: 'center', padding: 16 }}>Calculá para ver cuántos contactos entran.</div>
            ) : (
              <div style={{ textAlign: 'center', padding: 16 }}>
                <div style={{ fontFamily: 'Bricolage Grotesque', fontWeight: 800, fontSize: 40, color: 'var(--bisne-green-800)' }}>{count}</div>
                <div className="muted" style={{ fontSize: 13 }}>contactos entran al segmento</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

function SegmentDetail({ segment, onBack, onDeleted }: { segment: WebSegment; onBack: () => void; onDeleted: () => void }) {
  const { data: result, isLoading } = useQuery({ queryKey: ['segment-contacts', segment.id], queryFn: () => getSegmentContacts(segment.id) })
  const contacts = result?.data || []

  const deleteMutation = useMutation({
    mutationFn: () => deleteSegment(segment.id),
    onSuccess: () => { toast.success('Segmento eliminado'); onDeleted() },
    onError: (e: any) => toast.error(e?.message || 'No se pudo eliminar'),
  })

  return (
    <>
      <div className="page-header">
        <div className="page-title-block">
          <div className="row gap-2" style={{ marginBottom: 6 }}>
            <button onClick={onBack} type="button" style={{ fontSize: 12.5, color: 'var(--text-3)', background: 'none', border: 'none', cursor: 'pointer' }}>← Segmentos</button>
          </div>
          <h1>{segment.name}</h1>
          <div className="row gap-1" style={{ flexWrap: 'wrap', marginTop: 6 }}>
            {describeCriteria((segment.match || {}) as Criteria).map((p, i) => <Badge key={i} tone="neutral" size="sm">{p}</Badge>)}
          </div>
        </div>
        <div className="page-actions">
          <IconButton icon="trash2" tooltip="Eliminar segmento" onClick={() => { if (window.confirm(`¿Eliminar el segmento ${segment.name}?`)) deleteMutation.mutate() }} />
        </div>
      </div>

      <div className="table-wrap">
        {isLoading ? (
          <TableSkeleton columns={4} rows={8} />
        ) : contacts.length === 0 ? (
          <EmptyState icon="fingerprint" title="Sin contactos" desc="Ningún contacto entra en este segmento por ahora." />
        ) : (
          <table className="table">
            <thead><tr><th>Contacto</th><th style={{ textAlign: 'right' }}>Score</th><th>Origen</th><th>Convirtió</th></tr></thead>
            <tbody>
              {contacts.map((c) => (
                <tr key={c.id}>
                  <td><span className="cell-primary">{c.email || c.name || 'Anónimo'}</span></td>
                  <td style={{ textAlign: 'right' }}><span className="tabular">{c.score ?? 0}</span></td>
                  <td><span style={{ fontSize: 12.5, color: 'var(--text-2)' }}>{(c as any).first_utm_source || c.utm_source || '—'}</span></td>
                  <td>{c.converted ? <Badge tone="success" size="sm">Sí</Badge> : <Badge tone="neutral" size="sm">No</Badge>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  )
}
